import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Award, Heart, Zap, Flame, Star, Vote, Lock, MessageCircle, Shield, Crown, Swords } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  vote: Vote,
  fire: Flame,
  zap: Zap,
  heart: Heart,
  star: Star,
  message: MessageCircle,
  reply: MessageCircle,
  sword: Swords,
  shield: Shield,
  crown: Crown,
};

// Rarity color schemes with neon outlines
const rarityStyles = {
  common: {
    border: 'border-zinc-400/40',
    glow: '',
    bg: 'bg-zinc-400/10',
    iconColor: 'text-zinc-300',
    label: 'Common',
    labelColor: 'text-zinc-400',
    neon: 'shadow-[0_0_8px_rgba(161,161,170,0.3)]',
  },
  hard: {
    border: 'border-vent-secondary/50',
    glow: 'shadow-[0_0_12px_rgba(57,255,20,0.25)]',
    bg: 'bg-vent-secondary/10',
    iconColor: 'text-vent-secondary',
    label: 'Hard',
    labelColor: 'text-vent-secondary',
    neon: 'shadow-[0_0_12px_rgba(57,255,20,0.3)]',
  },
  unique: {
    border: 'border-vent-primary/50',
    glow: 'shadow-[0_0_14px_rgba(176,38,255,0.3)]',
    bg: 'bg-vent-primary/10',
    iconColor: 'text-vent-primary',
    label: 'Unique',
    labelColor: 'text-vent-primary',
    neon: 'shadow-[0_0_14px_rgba(176,38,255,0.35)]',
  },
  legendary: {
    border: 'border-yellow-500/50',
    glow: 'shadow-[0_0_16px_rgba(234,179,8,0.35)]',
    bg: 'bg-yellow-500/10',
    iconColor: 'text-yellow-400',
    label: 'Legendary',
    labelColor: 'text-yellow-400',
    neon: 'shadow-[0_0_16px_rgba(234,179,8,0.4)]',
  },
};

const BadgeItem = ({ badge }) => {
  const Icon = iconMap[badge.icon] || Award;
  const unlocked = badge.unlocked;
  const rarity = rarityStyles[badge.rarity] || rarityStyles.common;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={unlocked ? { scale: 1.05, y: -2 } : {}}
      className={`relative flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all border ${
        unlocked
          ? `${rarity.border} ${rarity.neon} bg-black/40`
          : 'border-white/5 bg-white/[0.02] opacity-30'
      }`}
      data-testid={`badge-${badge.id}`}
    >
      {!unlocked && (
        <Lock className="absolute top-1.5 right-1.5 w-3 h-3 text-zinc-700" />
      )}

      {/* Shield-style icon container */}
      <div className={`relative w-11 h-11 flex items-center justify-center ${
        unlocked ? rarity.bg : 'bg-zinc-900'
      }`} style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }}>
        <Icon className={`w-5 h-5 ${unlocked ? rarity.iconColor : 'text-zinc-700'}`} />
      </div>

      <div className="text-center">
        <p className={`text-[10px] font-heading font-bold leading-tight ${unlocked ? 'text-white' : 'text-zinc-700'}`}>
          {badge.name}
        </p>
        {unlocked && (
          <p className={`text-[8px] font-heading font-bold tracking-wider mt-0.5 ${rarity.labelColor}`}>
            {rarity.label}
          </p>
        )}
      </div>
    </motion.div>
  );
};

const BadgesSection = ({ sessionId }) => {
  const [data, setData] = useState(null);

  const fetchBadges = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/badges/${sessionId}`);
      setData(response.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('[Vent:badges]', err?.message);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) fetchBadges();
  }, [sessionId, fetchBadges]);

  if (!data) return null;

  const allBadges = [...data.badges, ...data.locked_badges];
  const seen = new Set();
  const uniqueBadges = allBadges.filter(b => {
    if (seen.has(b.id)) return false;
    seen.add(b.id);
    return true;
  });

  if (uniqueBadges.length === 0) return null;

  // Sort: unlocked first, then by rarity order
  const rarityOrder = { legendary: 0, unique: 1, hard: 2, common: 3 };
  const sorted = [...uniqueBadges].sort((a, b) => {
    if (a.unlocked !== b.unlocked) return a.unlocked ? -1 : 1;
    return (rarityOrder[a.rarity] || 3) - (rarityOrder[b.rarity] || 3);
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="badges-section"
    >
      <h3 className="font-heading text-lg sm:text-xl font-bold text-zinc-300 mb-4 flex items-center gap-2">
        <Shield className="w-5 h-5 text-vent-primary" />
        Your Badges
      </h3>
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        {sorted.map((badge, idx) => (
          <motion.div
            key={badge.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.04 }}
          >
            <BadgeItem badge={badge} />
          </motion.div>
        ))}
      </div>
      <div className="flex gap-4 mt-3 text-xs text-zinc-600">
        <span>Votes: {data.stats.votes}</span>
        <span>Reactions: {data.stats.reactions}</span>
        <span>Vents: {data.stats.vents || 0}</span>
        <span>Replies: {data.stats.replies || 0}</span>
      </div>
    </motion.div>
  );
};

export default BadgesSection;
